/**
 * ContentCard Component
 * Flippable content card for library grid
 */

import { t, getCurrentLanguage } from '../i18n/config.js';
import * as LikeService from '../services/likes.js';
import { ensureAudioUnlocked } from '../utils/audioUnlock.js';

export class ContentCard {
  constructor(container, contentData, router, profileData, initialLikeState = false) {
    this.container = container;
    this.contentData = contentData;
    this.router = router;
    this.profileData = profileData;
    this.element = null;
    this.isLiked = initialLikeState;
    this.isTogglingLike = false;
    this.lastLikeToggle = 0;
  }

  /**
   * Render content card with CSS flip effect
   * Optimized: Renders basic structure first, then adds details asynchronously
   */
  render() {
    const card = document.createElement('div');
    card.className = 'content-card';

    // Store activity type as data attribute for filtering
    if (this.contentData.activityType) {
      card.dataset.activityType = this.contentData.activityType.toLowerCase();
    }

    // Handle category as object or string (backward compatibility)
    const categoryId =
      typeof this.contentData.category === 'object'
        ? this.contentData.category?.id
        : this.contentData.category;

    if (categoryId) {
      card.dataset.category = categoryId.toLowerCase();
    }

    // Apply dynamic border color if available (only set if we have a valid color)
    // Skip default cyan (#44c1c5) — let CSS category variables handle it
    const borderColor =
      typeof this.contentData.category === 'object' ? this.contentData.category?.borderColor : null;

    if (borderColor && borderColor !== 'null' && borderColor !== '#44c1c5') {
      card.style.setProperty('--dynamic-border-color', borderColor);
    }

    // Check if content is available
    const isAvailable = this.contentData.isAvailable !== false;

    if (!isAvailable) {
      card.classList.add('content-card--unavailable');
    }

    // Render basic card structure immediately
    card.innerHTML = `
      <div class="card-inner">
        <!-- Front Face -->
        <div class="card-face card-face--front">
          ${this.renderFrontFace(isAvailable)}
        </div>

        <!-- Back Face -->
        <div class="card-face card-face--back">
          ${this.renderBackFace(isAvailable)}
        </div>
      </div>
    `;

    this.container.appendChild(card);
    this.element = card;

    console.log('🎴 Card element created:', {
      title: this.contentData.title,
      element: this.element,
    });

    // Delay non-critical rendering to next frame for smoother initialization
    requestAnimationFrame(() => {
      this.addCardInteractivity();
    });

    // Add image load handler for skeleton loader
    const coverImage = this.element.querySelector('.card-cover img');
    if (coverImage) {
      coverImage.addEventListener('load', () => {
        coverImage.classList.add('loaded');
      });
      // If image is already cached and loaded
      if (coverImage.complete) {
        coverImage.classList.add('loaded');
      }
    }
  }

  /**
   * Add card interactivity (event bindings)
   * Called asynchronously after card structure is rendered
   */
  addCardInteractivity() {
    this.bindEvents();
    this.observeNotificationIcon();
  }

  /**
   * Observe notification icon — trigger wiggle + hearts when it enters viewport
   * Repeats every time the icon scrolls back into view
   */
  observeNotificationIcon() {
    if (!this.element) return;
    const msgBtn = this.element.querySelector('.card-message-btn');
    if (!msgBtn || msgBtn.style.display === 'none') return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Restart wiggle animation
            msgBtn.classList.remove('notification-wiggle');
            void msgBtn.offsetWidth; // Force reflow to restart animation
            msgBtn.classList.add('notification-wiggle');

            // Spawn hearts rising from the notification icon for 2 seconds
            this._heartAnimationActive = true;
            this.spawnHeartParticles(msgBtn);
            setTimeout(() => {
              this._heartAnimationActive = false;
            }, 2000);

            // Remove wiggle class after animation completes
            msgBtn.addEventListener(
              'animationend',
              () => {
                msgBtn.classList.remove('notification-wiggle');
              },
              { once: true }
            );
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(msgBtn);
  }

  /**
   * Render front face
   */
  renderFrontFace(isAvailable) {
    // Use cover image from API, fallback to thumbnail, then default
    const coverImage =
      this.contentData.coverImageUrl ||
      this.contentData.coverImage ||
      this.contentData.thumbnail ||
      '/svg/branding/KassieAvatar.svg';

    const title = this.contentData.title || 'Untitled';
    const activityType = this.contentData.activityType || 'Read';

    // Handle category as object or string
    const categoryName =
      typeof this.contentData.category === 'object'
        ? this.contentData.category?.name
        : this.contentData.category || 'Stories';

    // Check if this is a published module (not coming-soon)
    const isPublished =
      this.contentData.status === 'published' ||
      !this.element ||
      !this.element.classList.contains('content-card--coming-soon');

    const isSingModule =
      this.contentData.activityType === 'sing' || this.contentData.activityType === 'Sing';
    const isWordSearch = this.contentData.activityType === 'word-search';
    const isGame = this.contentData.activityType === 'speel';
    let frontBadgeIcon = '/svg/icons/LeesIconGraphic.svg';
    let frontBadgeAlt = 'Read';
    if (isSingModule) {
      frontBadgeIcon = '/svg/icons/SingIcon.svg';
      frontBadgeAlt = 'Sing';
    } else if (isWordSearch || isGame) {
      frontBadgeIcon = '/svg/icons/SpeelIcon.svg';
      frontBadgeAlt = 'Game';
    }

    return `
      <button class="card-category-badge" aria-label="Category">
        <img src="${frontBadgeIcon}" alt="${frontBadgeAlt}" width="24" height="24" />
      </button>

      <div class="card-message-btn" aria-label="${t('child.card.newAudio') || 'New audio available'}" style="${this.contentData.hasFamilyRecording && !isSingModule ? '' : 'display: none;'}">
        <img src="/svg/components/Email.svg" alt="Message" />
      </div>

      <button class="card-info-btn" aria-label="${t('child.card.info') || 'Info'}">
        <img src="/svg/buttons/Info.svg" alt="Info" width="18" height="18" />
      </button>

      <div class="card-cover">

      <img src="${coverImage}" alt="${title}" />
      </div>

      <div class="card-content">
        <h3 class="card-title">${title}</h3>
        <div class="card-badges">
          <span class="card-badge card-badge--activity">${activityType}</span>
          <span class="card-badge card-badge--category">${categoryName}</span>
        </div>
      </div>

      ${isPublished
        ? `
        <button class="card-like-btn ${this.isLiked ? 'card-like-btn--liked' : ''}" aria-label="${this.isLiked ? t('child.card.unlike') || 'Unlike' : t('child.card.like') || 'Like'}">
          <img src="/svg/components/Wishheart.svg" alt="Like" />
        </button>
      `
        : ''
      }

      ${isWordSearch
        ? `
        <div class="card-wordsearch-overlay">
          <img src="${getCurrentLanguage() === 'en' ? '/svg/components/WordsearchOverlayEng.svg' : '/svg/components/WoordsoekoverlayAfr.svg'}" alt="Word Search" />
        </div>
      `
        : ''
      }

      <button class="card-play-btn" ${!isAvailable ? 'disabled' : ''}>
        <img src="/svg/buttons/PlayButton-2.svg" alt="${t('child.card.play') || 'Play'}" />
      </button>
    `;
  }

  /**
   * Render back face
   */
  renderBackFace(isAvailable) {
    const title = this.contentData.title || 'Untitled';
    const description = this.contentData.description || 'No description available.';

    // Format duration - for sing modules use songDuration (seconds → MM:SS), otherwise use estimatedDuration (minutes)
    const isSing =
      this.contentData.activityType === 'sing' || this.contentData.activityType === 'Sing';
    let duration;
    if (isSing && this.contentData.songDuration) {
      const mins = Math.floor(this.contentData.songDuration / 60);
      const secs = this.contentData.songDuration % 60;
      duration = `${mins}:${secs.toString().padStart(2, '0')}`;
    } else {
      const durationValue = this.contentData.estimatedDuration || 5;
      duration = `${durationValue} ${t('child.card.min')}`;
    }

    // Format age range from API (ageMin and ageMax are numbers)
    const ageMin = this.contentData.ageMin || 4;
    const ageMax = this.contentData.ageMax || 6;
    const ageRange = `${ageMin}-${ageMax} ${t('child.card.years')}`;

    // Get category badge (achievement badge) - NOT the category icon
    // badge_url = FarmBadge.svg, WildtuinBadge.svg, etc.
    // icon_url = PlaasVolume.svg, etc. (NOT what we want here)
    let categoryBadge =
      typeof this.contentData.category === 'object' ? this.contentData.category?.badgeUrl : null;

    // Normalize CDN URLs to local paths (badges are bundled in pwa/)
    if (categoryBadge && categoryBadge.includes('kassie.media/')) {
      categoryBadge = '/' + categoryBadge.split('kassie.media/')[1];
    }

    // Use category badge, fallback to FarmBadge
    const badgeIcon = categoryBadge || '/svg/components/FarmBadge.svg';

    if (!isAvailable) {
      return `
        <button class="card-category-badge" aria-label="Category">
          <img src="${badgeIcon}" alt="Category" width="34" height="34" />
        </button>

        <button class="card-info-btn" aria-label="${t('child.card.close') || 'Close'}">
          <img src="/svg/buttons/Info.svg" alt="Info" width="18" height="18" />
        </button>

        <div class="card-details">
          <h3 class="card-title">${title}</h3>
          <p class="card-description">${description}</p>
          <div class="card-meta">
            <span class="card-meta-item">⏱️ ${duration}</span>
            <span class="card-meta-item">👶 ${ageRange}</span>
          </div>
        </div>

        <div class="card-wishlist">
          <p class="card-wishlist-prompt">${t('child.card.addToWishlist') || 'Add to wishlist?'}</p>
          <button class="card-wishlist-btn" aria-label="${t('child.card.wishlist') || 'Add to wishlist'}">
            <svg viewBox="0 0 24 24" width="32" height="32" fill="currentColor">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
            </svg>
          </button>
        </div>
      `;
    }

    // Available content back face
    const earnedBadges = this.contentData.earnedBadges || [];

    return `
      <button class="card-category-badge" aria-label="Category">
        <img src="${badgeIcon}" alt="Category" width="34" height="34" />
      </button>

      <button class="card-info-btn" aria-label="${t('child.card.close') || 'Close'}">
        <img src="/svg/buttons/Info.svg" alt="Info" width="18" height="18" />
      </button>

      <div class="card-details">
        <h3 class="card-title">${title}</h3>
        <p class="card-description">${description}</p>
        <div class="card-meta">
          <span class="card-meta-item">⏱️ ${duration}</span>
          <span class="card-meta-item">👶 ${ageRange}</span>
        </div>
        ${earnedBadges.length > 0
        ? `
          <div class="card-earned-badges">
            ${earnedBadges
          .map(
            (badge) => `
              <span class="earned-badge">${badge.completed ? '⭐' : '☆'}</span>
            `
          )
          .join('')}
          </div>
        `
        : ''
      }
      </div>

      <button class="card-play-btn">
        <img src="/svg/buttons/PlayButton-2.svg" alt="${t('child.card.play') || 'Play'}" />
      </button>
    `;
  }

  /**
   * Bind events
   */
  bindEvents() {
    if (!this.element) return;

    // Info button (flip card using CSS)
    const infoButtons = this.element.querySelectorAll('.card-info-btn');
    console.log(`🎴 Found ${infoButtons.length} info buttons for card:`, this.contentData.title);
    infoButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        console.log('🔘 Info button clicked, calling flipCard()');
        this.flipCard();
      });
    });

    // Like button
    const likeBtn = this.element.querySelector('.card-like-btn');
    if (likeBtn) {
      // Unified handler for both click (desktop) and touch (mobile)
      const handleLikeInteraction = (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (!this.isLiked) {
          // Liking (gray → pink): spawn heart particles + wiggle
          this._heartAnimationActive = true;
          this.spawnHeartParticles(likeBtn);
          likeBtn.classList.add('card-like-btn--wiggle');
          setTimeout(() => {
            this._heartAnimationActive = false;
            likeBtn.classList.remove('card-like-btn--wiggle');
          }, 2000);
        } else {
          // Unliking (pink → gray): shrink and pop back
          this.playUnlikeAnimation(likeBtn);
        }
        this.handleLikeClick();
      };

      likeBtn.addEventListener('click', handleLikeInteraction);
      // Prevent double-fire on touch devices (touchend + click)
      likeBtn.addEventListener('touchend', (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleLikeInteraction(e);
      });
    }

    // Play button (bind to both front and back face buttons)
    const playBtns = this.element.querySelectorAll('.card-play-btn');
    playBtns.forEach((playBtn) => {
      if (playBtn && !playBtn.disabled) {
        playBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.playContent();
        });
      }
    });

    // Wishlist button
    const wishlistBtn = this.element.querySelector('.card-wishlist-btn');
    if (wishlistBtn) {
      wishlistBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.addToWishlist();
      });
    }
  }

  /**
   * Handle like button click with debouncing
   */
  async handleLikeClick() {
    // Debounce: prevent rapid toggling (300ms minimum between clicks)
    const now = Date.now();
    if (now - this.lastLikeToggle < 300) {
      console.log('❤️ Like click debounced');
      return;
    }
    this.lastLikeToggle = now;

    // Prevent concurrent toggles
    if (this.isTogglingLike) {
      console.log('❤️ Like toggle already in progress');
      return;
    }

    this.isTogglingLike = true;

    try {
      console.log('❤️ Toggling like for module:', this.contentData.id);

      // Get device ID from localStorage (using correct key)
      const deviceId = localStorage.getItem('kassie_device_id');
      if (!deviceId) {
        console.error('❌ No device ID found');
        this.showToast(t('child.errors.notAuthorized') || 'Not authorized');
        this.isTogglingLike = false;
        return;
      }

      // Toggle like via service (optimistic update handled by service)
      const result = await LikeService.toggleLike(
        this.contentData.id,
        this.profileData.id,
        deviceId
      );

      // Update local state
      this.isLiked = result.liked;

      // Update UI
      this.updateLikeButton();

      // Haptic feedback if supported
      if (navigator.vibrate) {
        navigator.vibrate(this.isLiked ? [10, 50, 10] : 10);
      }

      console.log('✅ Like toggled successfully:', {
        liked: result.liked,
        likesCount: result.likesCount,
      });
    } catch (error) {
      console.error('❌ Failed to toggle like:', error);
      this.showToast(t('child.errors.likeFailed') || 'Could not update like');
    } finally {
      this.isTogglingLike = false;
    }
  }

  /**
   * Update like button visual state
   */
  updateLikeButton() {
    if (!this.element) return;

    const likeBtn = this.element.querySelector('.card-like-btn');
    if (!likeBtn) return;

    if (this.isLiked) {
      likeBtn.classList.add('card-like-btn--liked');
      likeBtn.setAttribute('aria-label', t('child.card.unlike') || 'Unlike');
    } else {
      likeBtn.classList.remove('card-like-btn--liked');
      likeBtn.setAttribute('aria-label', t('child.card.like') || 'Like');
    }
  }

  /**
   * Play unlike animation — shrink to nothing over 500ms, then pop back at 1000ms
   */
  playUnlikeAnimation(likeBtn) {
    likeBtn.style.transition = 'transform 500ms ease-in, opacity 500ms ease-in';
    likeBtn.style.transform = 'scale(0)';
    likeBtn.style.opacity = '0';

    // Pop back after 1000ms total
    setTimeout(() => {
      likeBtn.style.transition =
        'transform 300ms cubic-bezier(0.175, 0.885, 0.32, 1.275), opacity 300ms ease';
      likeBtn.style.transform = 'scale(1)';
      likeBtn.style.opacity = '1';
    }, 1000);
  }

  /**
   * Spawn heart particle smoke effect on hover / touch-hold
   */
  spawnHeartParticles(likeBtn) {
    if (!this.element) return;

    this._heartAnimationActive = true;
    const card = this.element;
    const cardRect = card.getBoundingClientRect();
    const btnRect = likeBtn.getBoundingClientRect();

    // Spawn 10-15 hearts
    const count = 10 + Math.floor(Math.random() * 6);
    // Use offsetHeight for reliable measurement (getBoundingClientRect can be 0 with transforms)
    const cardHeight = card.offsetHeight || cardRect.height || 300;
    const maxTravelHeight = cardHeight * 0.75;

    for (let i = 0; i < count; i++) {
      const heart = document.createElement('img');
      heart.src = '/svg/components/Wishheart.svg';
      heart.className = 'heart-particle';

      // Random size 5-15px
      const size = 5 + Math.random() * 10;
      heart.style.width = size + 'px';
      heart.style.height = size + 'px';

      // Position at the like button center (relative to card)
      const startX = btnRect.left - cardRect.left + btnRect.width / 2 - size / 2;
      const startY = btnRect.top - cardRect.top + btnRect.height / 2 - size / 2;
      heart.style.left = startX + 'px';
      heart.style.top = startY + 'px';

      // Random delay for staggered effect
      const delay = Math.random() * 300;
      // Random sine wave frequency
      const frequency = 2 + Math.random() * 4;
      // Random horizontal amplitude
      const amplitude = 8 + Math.random() * 15;
      // Duration 800-1500ms
      const duration = 800 + Math.random() * 700;

      heart.style.opacity = '0.6';
      heart.style.position = 'absolute';
      heart.style.pointerEvents = 'none';
      heart.style.zIndex = '100';

      card.appendChild(heart);

      // Animate using requestAnimationFrame
      const startTime = performance.now() + delay;
      const self = this;
      const animate = (now) => {
        const elapsed = now - startTime;
        if (elapsed < 0) {
          // Still in delay — check if animation was cancelled (touch end)
          if (!self._heartAnimationActive) {
            heart.remove();
            return;
          }
          requestAnimationFrame(animate);
          return;
        }

        // If touch ended, fade out quickly and stop
        if (!self._heartAnimationActive) {
          heart.style.transition = 'opacity 200ms ease';
          heart.style.opacity = '0';
          setTimeout(() => heart.remove(), 200);
          return;
        }

        const progress = Math.min(elapsed / duration, 1);
        // Move up
        const y = startY - progress * maxTravelHeight;
        // Sine wave horizontal movement
        const x = startX + Math.sin(progress * Math.PI * frequency) * amplitude;
        // Fade out in last 40%
        const opacity = progress > 0.6 ? (1 - (progress - 0.6) / 0.4) * 0.6 : 0.6;

        heart.style.left = x + 'px';
        heart.style.top = y + 'px';
        heart.style.opacity = opacity;

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          heart.remove();
        }
      };

      requestAnimationFrame(animate);
    }
  }

  /**
   * Flip card using CSS
   */
  flipCard() {
    if (!this.element) {
      console.error('❌ Card element not found');
      return;
    }

    // Raise z-index above nav bar during flip animation
    this.element.style.zIndex = '1000';

    // Toggle flipped class
    this.element.classList.toggle('flipped');

    // Reset z-index after flip animation completes (~600ms)
    setTimeout(() => {
      this.element.style.zIndex = '';
    }, 600);

    // Haptic feedback if supported
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }
  }

  /**
   * Play content
   */
  playContent() {
    // iOS Safari only allows audio to start if the FIRST .play() call this
    // session happens synchronously inside a real user gesture's call stack.
    // The music route handler does `await import(...)` before rendering, and
    // the player itself used a setTimeout before its first .play() — either
    // one can push .play() past the end of this click's gesture window, which
    // Safari then silently rejects (NotAllowedError). ensureAudioUnlocked()
    // plays a silent buffer right here, synchronously (no await above it, and
    // this whole method is called directly from the button's click handler
    // with nothing async in between), which "spends" the gesture for the
    // whole session — every later .play() call (after the async
    // navigation/render below) is then allowed, because the audio pathway is
    // already unlocked, not because it is itself inside a gesture.
    // Idempotent (unlockAudio() is a one-time no-op after the first call), so
    // safe to call unconditionally even for non-music content types.
    // See REPORT_iOS_TOAST_LAYOUT_2026-08-09.md.
    ensureAudioUnlocked();

    console.log('▶️ Playing content:', this.contentData.title);
    console.log('▶️ Content data:', {
      id: this.contentData.id,
      title: this.contentData.title,
      type: this.contentData.type,
      activityType: this.contentData.activityType,
      fullData: this.contentData,
    });

    // Determine content type and navigate — use activityType first (more specific)
    const contentType = this.contentData.activityType || this.contentData.type;

    if (contentType === 'sing' || contentType === 'Sing') {
      // Navigate to music player
      this.router.navigate('music-player', this.profileData, this.contentData);
    } else if (contentType === 'word-search') {
      // Navigate to word search player
      this.router.navigate('word-search-player', this.profileData, this.contentData);
    } else if (contentType === 'speel' || contentType === 'game' || contentType === 'Play') {
      // Navigate to game player (memory games and other "speel" games)
      this.router.navigate('game-player', this.profileData, this.contentData);
    } else if (contentType === 'lees' || contentType === 'story' || contentType === 'Read') {
      // Navigate to story reader
      this.router.navigate('story-reader', this.profileData, this.contentData);
    } else {
      // Default: try story reader
      this.router.navigate('story-reader', this.profileData, this.contentData);
    }
  }

  /**
   * Add to wishlist
   */
  addToWishlist() {
    console.log('❤️ Adding to wishlist:', this.contentData.title);

    // Show toast notification
    this.showToast(t('child.card.comingSoon') || 'Coming soon!');

    // Haptic feedback if supported
    if (navigator.vibrate) {
      navigator.vibrate([10, 50, 10]);
    }
  }

  /**
   * Show toast notification
   */
  showToast(message) {
    // Create toast element
    const toast = document.createElement('div');
    toast.className = 'toast-notification';
    toast.textContent = message;

    document.body.appendChild(toast);

    // Trigger animation
    setTimeout(() => toast.classList.add('show'), 10);

    // Remove after 3 seconds
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  /**
   * Destroy card
   */
  destroy() {
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
  }
}
